import { useEffect, useMemo, useRef } from 'react';
import {
    MAX_PREVIEW_FILE_SIZE_BYTES,
    fetchTrajectoryRoleValues,
} from '@/features/datasets/utils/datasetUploadFlow';
import { sampleRecordsFromUploadEntry } from '@/features/datasets/utils/filePreviewUtils';
import {
    TRAJECTORY_ROLE_SCAN_SIZE_LIMIT_MESSAGE,
    TRAJECTORY_SAMPLE_ROWS,
    autoRoleMapping,
    mergeRoleValues,
    messageKeysFromRows,
    roleValuesFromRows,
    toolCallKeysFromRows,
} from './trajectoryMapping';

/** Sample rows inspected client-side before / instead of the whole-file scan. */
const SAMPLE_ROWS_FOR_TRAJECTORY = TRAJECTORY_SAMPLE_ROWS;

const SCANNABLE_FORMATS = new Set(['json', 'jsonl', 'parquet']);

/**
 * Keeps the trajectory block of a details object in sync with the picked
 * file: message keys and distinct role values come from the first sample
 * rows immediately, then from a whole-file `get-trajectory-role-values` scan
 * when the File handle is still around (dropped after upload, Next → Back).
 * The role → user/assistant/tool mapping is auto-filled on exact matches and
 * keeps the user's earlier picks for values that are still present.
 *
 * Returns the sample rows so the mapper can render the Role Example card.
 */
export function useTrajectoryRoleScan({ slot, details, onPatch, enabled }) {
    const sampleRows = useMemo(
        () => sampleRecordsFromUploadEntry(slot, SAMPLE_ROWS_FOR_TRAJECTORY),
        [slot]
    );
    const column = details?.trajectoryColumn || null;
    const roleKey = details?.trajectoryRoleKey || null;
    const file = slot?.file || null;
    const format = (slot?.format || '').toLowerCase();

    // Latest details / patcher without re-running the scan on every render.
    const detailsRef = useRef(details);
    detailsRef.current = details;
    const onPatchRef = useRef(onPatch);
    onPatchRef.current = onPatch;

    const scanKey =
        enabled && column
            ? [
                  slot?.id || '',
                  column,
                  roleKey || '',
                  file ? `${file.name}:${file.size}` : '',
                  sampleRows.length,
              ].join('|')
            : null;

    useEffect(() => {
        if (!scanKey) return undefined;
        let cancelled = false;
        const current = detailsRef.current || {};

        const sampleKeys = messageKeysFromRows(sampleRows, column);
        const sampleRoles = roleKey ? roleValuesFromRows(sampleRows, column, roleKey) : [];
        const wouldScan = Boolean(file && roleKey && SCANNABLE_FORMATS.has(format));
        // The backend rejects the whole file over the preview cap. Sample rows
        // already filled the mapping, so skip the request instead of blocking Next.
        const overScanLimit = wouldScan && file.size > MAX_PREVIEW_FILE_SIZE_BYTES;
        const canScan = wouldScan && !overScanLimit;
        // Values an earlier whole-file scan found (e.g. a `system` role only
        // deeper in the file) survive Next → Back, when the File handle is
        // gone; picking another column / role key resets them first.
        const roleValues = roleKey
            ? mergeRoleValues(sampleRoles, current.trajectoryRoleValues)
            : [];
        // Tool-call object keys (Map Tool Call Keys) come from the sample only;
        // a hydrated draft keeps its stored picks selectable next to them.
        const toolCallKeys = current.trajectoryToolCallKey
            ? mergeRoleValues(
                  toolCallKeysFromRows(sampleRows, column, current.trajectoryToolCallKey),
                  current.trajectoryToolCallKeys
              )
            : [];

        onPatchRef.current({
            trajectoryMessageKeys: mergeRoleValues(sampleKeys, current.trajectoryMessageKeys),
            trajectoryToolCallKeys: toolCallKeys,
            trajectoryRoleValues: roleValues,
            trajectoryRoleMapping: autoRoleMapping(roleValues, current.trajectoryRoleMapping),
            trajectoryRolesLoading: canScan,
            trajectoryError: overScanLimit
                ? TRAJECTORY_ROLE_SCAN_SIZE_LIMIT_MESSAGE
                : !canScan && roleKey && !sampleRoles.length && sampleRows.length
                  ? `No values found for '${roleKey}' inside '${column}'.`
                  : null,
        });
        if (!canScan) return undefined;

        fetchTrajectoryRoleValues({ file, fileType: format, trajectoryColumn: column, roleKey })
            .then((response) => {
                if (cancelled) return;
                const latest = detailsRef.current || {};
                if (!response?.success) {
                    onPatchRef.current({
                        trajectoryRolesLoading: false,
                        trajectoryError: response?.error || 'Could not read the trajectory roles',
                        // Keep whatever the scan managed to return (e.g. the
                        // too-many-values case) so the user sees the evidence.
                        ...(Array.isArray(response?.message_keys) && response.message_keys.length
                            ? {
                                  trajectoryMessageKeys: mergeRoleValues(
                                      latest.trajectoryMessageKeys,
                                      response.message_keys
                                  ),
                              }
                            : {}),
                    });
                    return;
                }
                const roleValues = mergeRoleValues(sampleRoles, response.role_values);
                onPatchRef.current({
                    trajectoryRolesLoading: false,
                    trajectoryError: null,
                    trajectoryMessageKeys: mergeRoleValues(
                        latest.trajectoryMessageKeys,
                        response.message_keys
                    ),
                    trajectoryRoleValues: roleValues,
                    trajectoryRoleMapping: autoRoleMapping(roleValues, latest.trajectoryRoleMapping),
                });
            })
            .catch((error) => {
                if (cancelled) return;
                onPatchRef.current({
                    trajectoryRolesLoading: false,
                    trajectoryError: error?.message || 'Could not read the trajectory roles',
                });
            });
        return () => {
            cancelled = true;
        };
        // `sampleRows`, `file` and `format` are folded into `scanKey`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scanKey]);

    return { sampleRows };
}
